import { AnalyticsEvent } from '~/modules/services/analyticsService';
import { getActiveExperiments, type WindowWithOptimizely } from '~/modules/utilities/experimentationUtils';
import { logger } from '~/modules/utilities/logger';
import { AnalyticsTarget } from './analyticsTarget';
import { GtmEventType } from './gtmEventTypes/gtmEventType';
import { GtmEventTypeFactory } from './gtmEventTypes/gtmEventTypeFactory';
import { getEnabledTrackedFlags } from './trackedFeatureFlags';

export class GoogleTagManager implements AnalyticsTarget {
    GA_ID = 'UA-45076517-23';
    postEvent(event: AnalyticsEvent): Promise<any> {
        const eventType: GtmEventType = new GtmEventTypeFactory().getEventTypeHandler(event);
        if (eventType) {
            const gtmEvent = eventType.getBody(event);
            if (eventType.isValidEvent(gtmEvent)) {
                const optimizelyAvailable = !!(window as WindowWithOptimizely).optimizely;
                const experiments = optimizelyAvailable ? getActiveExperiments() : [];
                const experimentsStatus = optimizelyAvailable ? 'active' : 'unavailable';
                const enabledFlags = getEnabledTrackedFlags();
                return Promise.resolve(
                    window.dataLayer.push({
                        ...gtmEvent,
                        experiments,
                        experimentsStatus,
                        ...(enabledFlags != null && { enabled_flags: enabledFlags }),
                    }),
                );
            }
            const invalidFields = eventType.getInvalidEventFields(gtmEvent);
            if (invalidFields.length > 0) {
                const invalidEvent = gtmEvent as { event?: unknown; listing_id?: unknown };
                logger.warn(
                    JSON.stringify({
                        message: 'GoogleTagManager dropped an invalid GA4 event',
                        context: 'googleTagManager.postEvent',
                        event_name: typeof invalidEvent.event === 'string' ? invalidEvent.event : undefined,
                        listing_id: typeof invalidEvent.listing_id === 'string' ? invalidEvent.listing_id : undefined,
                        invalid_fields: invalidFields,
                    }),
                );
            }
        }
        return Promise.resolve(null);
    }

    sendExperiment(experimentId: string, variantId: number): void {
        if (window.ga && window.ga.getAll) {
            // @ts-expect-error * Automated Type Error Fix
            const uaTracker = window.ga.getAll()?.find((tracker) => tracker.get('trackingId') === this.GA_ID);
            if (uaTracker) {
                const trackerName = uaTracker.get('name');
                const exp = `${experimentId}.${variantId}`;
                window.ga(`${trackerName}.set`, 'exp', exp);
                window.ga(`${trackerName}.send`, 'event', 'Experiment', 'Trigger', exp);
            }
        }
    }
}
